import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, createVNode as _createVNode, unref as _unref, createBlock as _createBlock, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5cc7a613"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "form-label given" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, "Given name", -1))
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "form-label surname" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, "Surname", -1))
const _hoisted_7 = ["disabled"]
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "help-text" }, " This profile will only exist online. ", -1))
const _hoisted_9 = { class: "add-relationship" }
const _hoisted_10 = { class: "form-check-label" }

import { computed, nextTick, ref, watch } from 'vue'
import { createProfilePersonAsync, getNewProfileRelationshipGender, getNewProfileSearchRelatedPlaceholder, getOrCreateChildRelationshipAsync, getOrCreateProfileFamilyAsync, getOrCreateSpouseRelationshipAsync, isChildRelationship, isParentRelationship, isSpouseRelationship, NewProfileRelationship } from './ProfileManager'
import { FamilyRelationshipRole, ItemType, ViewPerson } from '@/rd/ResearchDataModel'
import Modal from '@/util/Modal.vue'
import ProfileRelationshipSelector from '../profiles/ProfileRelationshipSelector.vue'
import PersonSelector from '../PersonSelector.vue'
import FamilySelector from '../FamilySelector.vue'
import { CompositeId } from '@/rd/CompositeId'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useTokenManager } from '@/auth/TokenManager'
import { LoadMode } from '@/util/AsyncData'


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileAddModal',
  props: {
    title: {}
  },
  emits: ["added"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





__expose({
  open: () => modalRef.value?.open()
})

const givenRef = ref('')
const surnameRef = ref('')
const addRelationshipRef = ref(false)
const newRelationshipRef = ref(NewProfileRelationship.Son)
const relatedItemIdRef = ref<string>()
const relatedItemErrorRef = ref<string>()
const busyRef = ref(false)

const modalRef = ref<InstanceType<typeof Modal>>()
const givenInputRef = ref<HTMLInputElement>()

const dataGroupStore = useDataGroupStore()
const tokenManager = useTokenManager()

const hasNameRef = computed(() => !!givenRef.value.trim() || !!surnameRef.value.trim())
const relatedItemTypeRef = computed(() => isChildRelationship(newRelationshipRef.value) ? ItemType.Family : ItemType.Person)
const relatedItemSearchPlaceholderRef = computed(() => getNewProfileSearchRelatedPlaceholder(newRelationshipRef.value))
const canAddProfileRef = computed(() => hasNameRef.value && (!addRelationshipRef.value || (!!relatedItemIdRef.value && !relatedItemErrorRef.value)))

watch(newRelationshipRef, () => relatedItemIdRef.value = undefined)

watch([newRelationshipRef, relatedItemIdRef], async () => {
  relatedItemErrorRef.value = ''

  if (!relatedItemIdRef.value)
    return // obviously invalid

  if (isParentRelationship(newRelationshipRef.value)) {
    const relatedGroupId = CompositeId.getGroupId(relatedItemIdRef.value)
    const relatedGroup = await dataGroupStore.getAsyncGroup(relatedGroupId, LoadMode.EnsureLoaded)?.whenLoadCompleted
    if (relatedGroup?.ownerId != tokenManager.current.userId) {
      relatedItemErrorRef.value = "You don't have permission to create a new parent for this profile."
      return
    }
  }
})

async function onShown() {
  givenRef.value = ''
  surnameRef.value = ''
  addRelationshipRef.value = false
  newRelationshipRef.value = NewProfileRelationship.Son
  relatedItemIdRef.value = undefined
  relatedItemErrorRef.value = ''

  await nextTick()
  givenInputRef.value?.focus()
}

async function getSelectableRelatedPersonsAsync(vps: ViewPerson[]) {
  return vps.filter(vp => !vp.assumeDeceased)
}

async function onCreateProfile() {
  busyRef.value = true

  try {
    const np = {
      gender: addRelationshipRef.value ? getNewProfileRelationshipGender(newRelationshipRef.value) : undefined,
      givenName: givenRef.value.trim(),
      surname: surnameRef.value.trim(),
    }
    const newProfilePersonId = await createProfilePersonAsync(np)

    if (addRelationshipRef.value) {
      await createRelationshipAsync(newProfilePersonId)
    }

    await modalRef.value?.close()
    emit('added', newProfilePersonId)
  }
  finally {
    busyRef.value = false
  }
}

async function createRelationshipAsync(profilePersonId: string) {
  if (!relatedItemIdRef.value)
    throw 'Cannot create profile because no related person or family selected'

  if (isChildRelationship(newRelationshipRef.value)) {
    if (relatedItemTypeRef.value != ItemType.Family)
      throw 'Related item when creating a child profile must be a family'

    await getOrCreateChildRelationshipAsync(profilePersonId, relatedItemIdRef.value)
  }
  else {
    if (relatedItemTypeRef.value != ItemType.Person)
      throw 'Related item when creating a spouse or parent profile must be a person'

    if (isSpouseRelationship(newRelationshipRef.value)) {
      const spouseRole = newRelationshipRef.value == NewProfileRelationship.Husband 
        ? FamilyRelationshipRole.Father
        : FamilyRelationshipRole.Mother
      await getOrCreateSpouseRelationshipAsync(relatedItemIdRef.value, profilePersonId, spouseRole)
    }
    else {
      // create family in (new) parent profile group
      const profileGroupId = CompositeId.getGroupId(profilePersonId)!
      const fatherId = newRelationshipRef.value == NewProfileRelationship.Father ? profilePersonId : undefined
      const motherId = newRelationshipRef.value == NewProfileRelationship.Mother ? profilePersonId : undefined
      const profileFamily = await getOrCreateProfileFamilyAsync(profileGroupId, fatherId, motherId)

      // create child relationship in child profile group
      await getOrCreateChildRelationshipAsync(relatedItemIdRef.value, profileFamily.id!)
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    ref_key: "modalRef",
    ref: modalRef,
    "ok-disabled": !canAddProfileRef.value,
    onShown: onShown,
    onOk: onCreateProfile
  }, {
    title: _withCtx(() => [
      (_ctx.$slots.title)
        ? _renderSlot(_ctx.$slots, "title", { key: 0 })
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createTextVNode("Add a profile")
          ], 64))
    ]),
    okText: _withCtx(() => [
      _createTextVNode(" Add profile ")
    ]),
    default: _withCtx(() => [
      (_ctx.$slots.instructions)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _renderSlot(_ctx.$slots, "instructions")
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("label", _hoisted_2, [
        _hoisted_3,
        _withDirectives(_createElementVNode("input", {
          type: "text",
          id: "givenInput",
          ref_key: "givenInputRef",
          ref: givenInputRef,
          disabled: busyRef.value,
          class: "form-control",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((givenRef).value = $event))
        }, null, 8, _hoisted_4), [
          [_vModelText, givenRef.value]
        ])
      ]),
      _createElementVNode("label", _hoisted_5, [
        _hoisted_6,
        _withDirectives(_createElementVNode("input", {
          type: "text",
          id: "surnameInput",
          disabled: busyRef.value,
          class: "form-control",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((surnameRef).value = $event))
        }, null, 8, _hoisted_7), [
          [_vModelText, surnameRef.value]
        ])
      ]),
      _hoisted_8,
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("label", _hoisted_10, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            class: "form-check-input",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((addRelationshipRef).value = $event))
          }, null, 512), [
            [_vModelCheckbox, addRelationshipRef.value]
          ]),
          (addRelationshipRef.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode(" Add as a "),
                _createVNode(ProfileRelationshipSelector, {
                  modelValue: newRelationshipRef.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newRelationshipRef).value = $event))
                }, null, 8, ["modelValue"]),
                _createTextVNode(" to: ")
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createTextVNode(" Add a relationship to an existing person ")
              ], 64))
        ]),
        (addRelationshipRef.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (relatedItemTypeRef.value == _unref(ItemType).Person)
                ? (_openBlock(), _createBlock(PersonSelector, {
                    key: 0,
                    modelValue: relatedItemIdRef.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((relatedItemIdRef).value = $event)),
                    placeholder: relatedItemSearchPlaceholderRef.value,
                    "post-filter": getSelectableRelatedPersonsAsync,
                    disabled: busyRef.value
                  }, null, 8, ["modelValue", "placeholder", "disabled"]))
                : (_openBlock(), _createBlock(FamilySelector, {
                    key: 1,
                    modelValue: relatedItemIdRef.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((relatedItemIdRef).value = $event)),
                    placeholder: relatedItemSearchPlaceholderRef.value,
                    disabled: busyRef.value
                  }, null, 8, ["modelValue", "placeholder", "disabled"]))
            ], 64))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 3
  }, 8, ["ok-disabled"]))
}
}

})